import React, { useState } from 'react';
import { Menu, X, Mail, Github, Linkedin, Network, BarChart2, Users, Mountain, Settings, GitCommit, Workflow, ArrowRight } from 'lucide-react';

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <nav className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <span className="text-xl font-bold bg-gradient-to-r from-gray-100 to-gray-300 bg-clip-text text-transparent">
                Shane Wood
              </span>
              <span className="ml-4 text-sm text-gray-400">
                Supply Chain Strategist • Transformational Leader • Outdoor Enthusiast
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#about" className="text-gray-300 hover:text-gray-100 flex items-center">
                About
                <ArrowRight size={16} className="ml-1" />
              </a>
              <a href="#work" className="text-gray-300 hover:text-gray-100 flex items-center">
                Work
                <ArrowRight size={16} className="ml-1" />
              </a>
              <a href="#musings" className="text-gray-300 hover:text-gray-100 flex items-center">
                Musings
                <ArrowRight size={16} className="ml-1" />
              </a>
              <a href="#contact" className="text-gray-300 hover:text-gray-100 flex items-center">
                Contact
                <ArrowRight size={16} className="ml-1" />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-gray-300"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden py-2">
              <a href="#about" className="block py-2 text-gray-300 hover:text-gray-100 flex items-center">
                <ArrowRight size={16} className="mr-2" />
                About
              </a>
              <a href="#work" className="block py-2 text-gray-300 hover:text-gray-100 flex items-center">
                <ArrowRight size={16} className="mr-2" />
                Work
              </a>
              <a href="#musings" className="block py-2 text-gray-300 hover:text-gray-100 flex items-center">
                <ArrowRight size={16} className="mr-2" />
                Musings
              </a>
              <a href="#contact" className="block py-2 text-gray-300 hover:text-gray-100 flex items-center">
                <ArrowRight size={16} className="mr-2" />
                Contact
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* AI Deployment Banner */}
      <div className="bg-gray-800 py-3">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-gray-300 text-sm flex items-center justify-center">
            <GitCommit size={16} className="mr-2" />
            <span className="font-semibold">Fun Fact:</span> This website was self-deployed in January 2025 using publicly available tools and AI assistance. 
            Yes, I learned to code (a little) and deploy a website! 🚀
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gray-900 relative">
        <div className="max-w-7xl mx-auto py-24 px-4">
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <Network size={48} className="text-gray-300" />
            </div>
            <h1 className="text-5xl font-bold bg-gradient-to-r from-gray-100 to-gray-300 bg-clip-text text-transparent sm:text-6xl">
              Supply Chain Strategy & Transformation
            </h1>
            <p className="mt-6 text-xl text-gray-300 flex items-center justify-center">
              <BarChart2 size={24} className="mr-2" />
              Driving excellence through process innovation and strategic leadership
            </p>
          </div>
        </div>
      </div>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center mb-8">
            <Users size={32} className="text-gray-300 mr-3" />
            <h2 className="text-3xl font-bold text-gray-100">About Me</h2>
          </div>
          <div className="prose prose-lg text-gray-300">
            <p className="mb-4">
              With over 20 years of supply chain expertise, I transform organizations through process innovation and strategic leadership. My focus spans E2E supply chain optimization, inventory management, and building high-performing teams that deliver sustainable results.
            </p>
            <p className="mb-4">
              Based in Denver with my family, we embrace the Colorado lifestyle where outdoor adventures fuel creative problem-solving and leadership insights. This balance of professional drive and outdoor exploration shapes my approach to both business transformation and life.
            </p>
            <p>
              Success comes from strategic vision combined with tactical execution. Whether leading teams or mapping processes, I believe in building resilient, adaptable solutions that stand the test of time.
            </p>
          </div>
        </div>
      </section>

      {/* Work Section */}
      <section id="work" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-100 mb-12 flex items-center">
            <Settings size={32} className="mr-3" />
            My Work
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-gray-300 transition-colors duration-300">
              <div className="flex items-center mb-4">
                <Network className="text-gray-300 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-gray-100">E2E Supply Chain Strategy</h3>
              </div>
              <p className="text-gray-300">Drive end-to-end supply chain transformation through advanced analytics, process optimization, and strategic inventory management solutions.</p>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-gray-300 transition-colors duration-300">
              <div className="flex items-center mb-4">
                <Settings className="text-gray-300 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-gray-100">Process Innovation & Technology</h3>
              </div>
              <p className="text-gray-300">Leverage cutting-edge technology and continuous improvement methodologies to streamline operations and enhance supply chain visibility.</p>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-gray-300 transition-colors duration-300">
              <div className="flex items-center mb-4">
                <Users className="text-gray-300 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-gray-100">Strategic Leadership</h3>
              </div>
              <p className="text-gray-300">Build and lead high-performing teams that drive sustainable transformation through strategic thinking and operational excellence.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Musings Section */}
      <section id="musings" className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-100 mb-12 flex items-center">
            <GitCommit size={32} className="mr-3" />
            Musings
          </h2>
          <div className="space-y-8">
            <article className="bg-gray-900 p-8 rounded-lg border border-gray-700 hover:border-gray-300 transition-colors duration-300">
              <div className="flex items-center mb-4">
                <Workflow className="text-gray-300 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-gray-100">
                  Supply Chain Innovation Through Technology
                </h3>
              </div>
              <p className="text-sm text-gray-400 mb-4">Strategic Transformation</p>
              <p className="text-gray-300">
                Exploring how strategic technology implementation and process optimization are revolutionizing supply chain operations, from inventory management to network design.
              </p>
            </article>

            <article className="bg-gray-900 p-8 rounded-lg border border-gray-700 hover:border-gray-300 transition-colors duration-300">
              <div className="flex items-center mb-4">
                <Mountain className="text-gray-300 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-gray-100">
                  Mountain Perspectives on Leadership
                </h3>
              </div>
              <p className="text-sm text-gray-400 mb-4">Leadership Insights</p>
              <p className="text-gray-300">
                Drawing parallels between Colorado's mountain adventures and supply chain leadership - where strategic planning, adaptability, and team trust create paths to success.
              </p>
            </article>

            <article className="bg-gray-900 p-8 rounded-lg border border-gray-700 hover:border-gray-300 transition-colors duration-300">
              <div className="flex items-center mb-4">
                <GitCommit className="text-gray-300 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-gray-100">
                  Building Resilient Supply Chains
                </h3>
              </div>
              <p className="text-sm text-gray-400 mb-4">Strategic Implementation</p>
              <p className="text-gray-300">
                Insights on developing robust supply chain solutions through strategic process mapping, continuous improvement, and team empowerment.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-100 mb-12 flex items-center">
            <Mail size={32} className="mr-3" />
            Get in Touch
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="mt-1 block w-full rounded-md bg-gray-800 border-gray-700 text-gray-100 focus:border-gray-300 focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="mt-1 block w-full rounded-md bg-gray-800 border-gray-700 text-gray-100 focus:border-gray-300 focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300">
                    Message
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    className="mt-1 block w-full rounded-md bg-gray-800 border-gray-700 text-gray-100 focus:border-gray-300 focus:ring-gray-300"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-gray-300 to-gray-400 text-gray-900 py-2 px-4 rounded-md hover:from-gray-400 hover:to-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 flex items-center justify-center"
                >
                  <Mail size={20} className="mr-2" />
                  Send Message
                </button>
              </form>
            </div>

            <div className="space-y-6">
              <p className="text-gray-300">
                Let's connect and discuss how strategic supply chain transformation can drive your organization's success.
              </p>
              <div className="flex items-center space-x-3">
                <Mail className="text-gray-300" />
                <span className="text-gray-300">shane@shanewoodworks.com</span>
              </div>
              <div className="flex space-x-6">
                <a href="https://www.linkedin.com/in/shane-wood-a607592b" className="text-gray-400 hover:text-gray-300 transition-colors duration-200">
                  <Linkedin size={24} />
                </a>
                <a href="mailto:shane@shanewoodworks.com" className="text-gray-400 hover:text-gray-300 transition-colors duration-200">
                  <Mail size={24} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-gray-800 border-t border-gray-700">
        <div className="max-w-7xl mx-auto py-8 px-4">
          <p className="text-center text-gray-400">
            © {new Date().getFullYear()} Shane Wood. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;
